import React from 'react'

import classNamesBind from 'classnames/bind'
import { LoadingStates } from '@dashboard/lib/types'
import { openWindow } from '@dashboard/lib/browser'

import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

type ShareActionType =
  | 'twitter'
  | 'linkedin'
  | 'facebook'
  | 'print'
  | 'copy'
  | 'like'

export interface StateProps {
  shortenStates: Record<string, LoadingStates | undefined>
  shortenResults: Record<string, string | undefined>
}

export interface DispatchProps {
  shorten: (url: string) => void
}

export interface OwnProps {
  active?: boolean
  row?: boolean
  tweet: {
    text: string
    hashtags?: string[]
  }
  hideLikeButton?: boolean
  overrideActions?: (action: ShareActionType) => void
  responsiveHorizontal?: boolean
}

interface State {
  linkCopied: boolean
}

type Props = DispatchProps & StateProps & OwnProps

export default class ShareActions extends React.Component<Props, State> {
  timeout: number | null = null

  constructor(props: Props) {
    super(props)

    this.state = {
      linkCopied: false,
    }
  }

  componentDidMount() {
    this.getShortenedURL()
    this.initFacebook()
  }

  componentDidUpdate() {
    this.getShortenedURL()
  }

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  render() {
    const { active, responsiveHorizontal, row } = this.props

    return (
      <div
        className={classNames(
          'container',
          { row },
          {
            active: active === true || active === undefined,
            responsiveHorizontal,
          },
        )}
      >
        <div className={css.buttons}>
          <img
            className={css.socialIcon}
            src="/static/share/images/twitter-natural.svg"
            alt="share to twitter"
            onClick={this.shareOnTwitter}
          />
          <img
            className={css.socialIcon}
            src="/static/share/images/linkedin-natural.svg"
            alt="share to LinkedIn"
            onClick={this.shareOnLI}
          />
          <img
            className={css.socialIcon}
            src="/static/share/images/facebook-natural.svg"
            alt="share to facebook"
            onClick={this.postToFacebook}
          />
          <img
            className={css.socialIcon}
            src="/static/share/images/print.svg"
            alt="print page"
            onClick={this.print}
          />
          <img
            className={css.socialIcon}
            src="/static/share/images/large-link.svg"
            alt="copy link icon"
            onClick={this.copyLink}
          />
          {this.renderLikeButton()}
        </div>
        {this.renderLinkCopiedNotification()}
      </div>
    )
  }

  renderLikeButton() {
    const { hideLikeButton } = this.props

    if (hideLikeButton) return null

    return (
      <div
        className={`fb-like ${css.facebookLikeButton}`}
        data-href="https://www.facebook.com/crystalknowsme/"
        data-layout="button"
        data-action="like"
        data-size="small"
        data-show-faces="true"
        data-share="false"
      />
    )
  }

  renderLinkCopiedNotification() {
    const { linkCopied } = this.state

    return (
      <div className={classNames('linkCopied', { active: linkCopied })}>
        Link Copied!
      </div>
    )
  }

  // Functions
  shortenResult(): string {
    const { shortenResults } = this.props
    const shortURL = shortenResults[window.location.href]

    return shortURL || window.location.href
  }

  postToFacebook = () => {
    const { overrideActions } = this.props

    if (overrideActions) {
      overrideActions('facebook')
    } else {
      openWindow({
        path: `https://www.facebook.com/sharer/sharer.php?display=popup&u=${encodeURIComponent(
          this.shortenResult(),
        )}`,
        callback: () => null,
        windowName: 'PostToFacebook',
        width: 625,
        height: 436,
      })
    }
  }

  print = () => {
    const { overrideActions } = this.props

    if (overrideActions) {
      overrideActions('print')
    } else {
      window.print()
    }
  }

  shareOnTwitter = () => {
    const { tweet, overrideActions } = this.props
    let path = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      tweet.text.replace(/<url>/g, this.shortenResult()),
    )}`

    if (overrideActions) {
      overrideActions('twitter')
    } else {
      if (tweet.hashtags) path += `&hashtags=${tweet.hashtags.join(',')}`

      openWindow({
        path,
        callback: () => null,
        windowName: 'PostToTwitter',
        width: 625,
        height: 436,
      })
    }
  }

  shareOnLI = () => {
    const { overrideActions } = this.props

    if (overrideActions) {
      overrideActions('linkedin')
    } else {
      openWindow({
        path: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURI(
          this.shortenResult(),
        )}`,
        callback: () => null,
        windowName: 'PostToLinkedIn',
        width: 625,
        height: 436,
      })
    }
  }

  copyLink = () => {
    const { overrideActions } = this.props

    if (overrideActions) {
      overrideActions('copy')
    } else {
      this.copyText(this.shortenResult())
      this.setState({ linkCopied: true }, () => {
        if (this.timeout) clearTimeout(this.timeout)

        this.timeout = window.setTimeout(
          () => this.setState({ linkCopied: false }),
          2000,
        )
      })
    }
  }

  copyText(text: string) {
    const input = document.createElement('input')
    input.value = text
    input.style.opacity = '0'
    input.style.position = 'fixed'
    document.body.appendChild(input)
    input.select()
    input.setSelectionRange(0, 99999)
    document.execCommand('copy')
    document.body.removeChild(input)
  }

  initFacebook() {
    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src =
      'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.0'
    script.async = true
    document.body.appendChild(script)

    type WindowWithFB = Window & { fbAsyncInit: () => void }

    const otherWindow = window as unknown as WindowWithFB
    otherWindow.fbAsyncInit = function () {
      window.FB.XFBML.parse()
    }
  }

  getShortenedURL() {
    const { shorten, shortenStates } = this.props

    if ((shortenStates[window.location.href] || 'default') === 'default')
      shorten(window.location.href)
  }
}
